import axiosInstance from "@/middleware/axios";
import { Query } from "@/types/queryTpes";
import { chatEndpoints, chatPublicEndpoints } from "./endpoints";
import {
  ChatConversationSchemaType,
  ChatFullConversationSchemaType,
  ChatMessagesUnreadCountSchemaType,
  MessageAttachmentSchemaType,
} from "./schemas";
import { SendMessageSuccessResponseType } from "../../auth/api/schemas";
import { setAuthTokens } from "@/lib/cookieManipulation";

const getUserConversations = async ({
  userId,
}: {
  userId: string;
}): Promise<Query<ChatConversationSchemaType[]>> => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }
    const response = await axiosInstance.get(
      chatEndpoints.getUserConversations()
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Fetching User Conversations: " + error);
  }
};
const getConversationByIdAction = async ({
  userId,
  conversationId,
  pageParam,
}: {
  userId: string;
  conversationId: string;
  pageParam?: number;
}): Promise<Query<ChatFullConversationSchemaType>> => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }
    const response = await axiosInstance.get(
      chatEndpoints.getConversation(conversationId, pageParam)
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Fetching Conversation: " + error);
  }
};

const uploadChatImagesAction = async ({
  files,
  onProgress,
}: {
  files: File[];
  onProgress?: (percent: number) => void;
}): Promise<Query<MessageAttachmentSchemaType[]>> => {
  try {
    const formData = new FormData();
    files.forEach((file, i) => formData.append(`images[${i}]`, file));
    const response = await axiosInstance.post(
      chatEndpoints.uploadImages(),
      formData,
      {
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Uploading Chat Images: " + error);
  }
};

const deleteConversationAction = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<Query<{ deleted: boolean }>> => {
  try {
    const response = await axiosInstance.delete(
      chatEndpoints.deleteConversation(conversationId)
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Deleting Conversation: " + error);
  }
};

const getUserChatUnreadCount = async ({
  userId,
}: {
  userId: string;
}): Promise<Query<ChatMessagesUnreadCountSchemaType>> => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }
    const response = await axiosInstance.get(
      chatEndpoints.getUserUnreadCount()
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Fetching User Unread Count: " + error);
  }
};

const createLeadConversationAction = async ({
  message,
  email,
  recipientId,
  realEstateId,
  name,
  password,
}: {
  message: string;
  email: string;
  recipientId: string;
  realEstateId?: string;
  name: string;
  password: string;
}): Promise<Query<SendMessageSuccessResponseType>> => {
  try {
    const response = await axiosInstance.post<
      Query<SendMessageSuccessResponseType>
    >(chatPublicEndpoints.createMessageGuestUser, {
      message,
      email,
      recipientId,
      realEstateId,
      name,
      password,
    });
    const { accessToken } = response.data.data.tokens;

    const expiresIn = 3600;
    setAuthTokens(accessToken, expiresIn);

    if (typeof window !== "undefined") {
      localStorage.removeItem("refreshAttempted");
    }
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Creating Lead Conversation: " + error);
  }
};

const createGoogleLeadConversationAction = async ({
  googleId,
  email,
  name,
  picture,
  recipientId,
  message,
  realEstateId,
}: {
  googleId: string;
  email: string;
  name: string;
  picture: string;
  recipientId: string;
  message: string;
  realEstateId?: string;
}): Promise<Query<SendMessageSuccessResponseType>> => {
  try {
    const response = await axiosInstance.post<
      Query<SendMessageSuccessResponseType>
    >(chatPublicEndpoints.createMessageGoogleGuestUser, {
      googleId,
      email,
      name,
      picture,
      recipientId,
      message,
      realEstateId,
    });

    const { accessToken } = response.data.data.tokens;

    const expiresIn = 3600;
    setAuthTokens(accessToken, expiresIn);

    if (typeof window !== "undefined") {
      localStorage.removeItem("refreshAttempted");
    }

    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Creating Google Lead Conversation: " + error);
  }
};

export {
  getUserConversations,
  getUserChatUnreadCount,
  getConversationByIdAction,
  deleteConversationAction,
  createLeadConversationAction,
  createGoogleLeadConversationAction,
  uploadChatImagesAction,
};
